import { makeAutoObservable } from "mobx"
import { MyProfileResponse } from "../models/api/MyProfileResponse"
import { MyProfileUpdateRequest } from "../models/api/MyProfileUpdateRequest"
import { ProgStatus } from "../models/utilTypes"
import ApiService from "../services/ApiService"
import { rootStore } from "../StApp"
import * as Base64_Blob from 'base64-blob'
import moment from "moment"
import { DOB_FORMAT } from "../models/constants"
import { fetchVIntroUrl, fetchVNoteAudioUrl } from "../services/HelperService"
import { Prompts } from "../models/api/Prompts"
import { ProfPrompt } from "../models/app/ProfPrompt"
import { GoogleLoginResponse } from "react-google-login"
import { UpdateFedEmailRequest } from "../models/api/UpdateFedEmailRequest"
import Logger from "../services/Logger"
import { GeocodeMetadata } from "../models/api/GeocodeMetadata"

export class ProfileStore{

    name: string = ''
    nickName: string = ''
    place: string = ''
    email: string|null = ''
    picData0: string = ''
    picData1: string = ''
    picData2: string = ''
    picData3: string = ''
    dob: string = ''
    bio: string = ''
    voiceIntroData:string = 'same'
    voiceIntrolUrl: string = ''
    myProfileResp?: MyProfileResponse
    profileLoadProg: ProgStatus = 'LOADING'
    updateProfileLoadProg: ProgStatus = 'INIT'
    deleteText = ''
    deleteProfileProg: ProgStatus = 'INIT'
    profileVisits = 0
    vIntroVisits = 0
    likesGot = 0
    shownCount = 0
    dumpCount = 0
    rightSwipes = 0
    leftSwipes = 0
    matches = 0
    dateEnabled: boolean = true
    geoLocation: GeocodeMetadata|null = null
    prompts: ProfPrompt[] = []
    
    private sex: string =''

    constructor(){
        makeAutoObservable(this)
    }

    updateName(data:string){
        this.name = data
    }

    updateNickName(data:string){
        this.nickName = data
    }

    updatePlace(data:string){
        this.place = data
    }

    updateEmail(data:string){
        this.email = data
    }

    setPicData0(data: string){
        this.picData0 = data
    }

    setPicData1(data: string){
        this.picData1 = data
    }

    setPicData2(data: string){
        this.picData2 = data
    }

    setPicData3(data: string){
        this.picData3 = data
    }

    async setPicData(file: File, index: number){
        const blobData = await Base64_Blob.blobToBase64(file)
        const blobString = blobData.split(',')[1]
        this.setPic(index, blobString)
    }

    deletePic(index: number){
        this.setPic(index, 'delete')
    }

    private setPic(index: number, data: string){

        switch(index){
            case 0: this.setPicData0(data)
            break;

            case 1: this.setPicData1(data)
            break;

            case 2: this.setPicData2(data)
            break;

            case 3: this.setPicData3(data)
            break;
            
        }

    }

    async setVoiceIntroData(data: Blob){
        this.voiceIntroData = (await Base64_Blob.blobToBase64(data)).split(',')[1] 
    }

    deleteVoiceIntroData(){
        this.voiceIntroData = 'delete'
    }

    updateDob(data: string){

        
        // If dob is set, do not allow changing
        if(this.myProfileResp?.dob != null)
         return

        // Do not allow more then 10 chars
        if(data.length > 10)
         return

        if(data.length == 10){
            let start = moment(data, DOB_FORMAT)
            let end = moment()
            let diffYears = moment.duration(end.diff(start)).asYears()
            if(diffYears < 18)
             return 
        }

         // Add formatter
        if((data.length == 2 && this.dob.length == 1 )|| (data.length == 5 && this.dob.length == 4 ))
         this.dob = data+'/'
        else
         this.dob = data
    }

    updateBio(data: string){
        this.bio = data
    }

    updateDeleteText(data: string){
        this.deleteText = data
    }

    updateSameVoiceIntro(){
        this.voiceIntroData = 'same'
    }

    deleteVoiceIntro(){
        this.voiceIntroData = 'delete'
    }

    toggleDateEnabled(){

        this.dateEnabled = !this.dateEnabled
        ApiService.updateDateEnabled(this.dateEnabled)
            .catch(err =>{
                this.dateEnabled = !this.dateEnabled
            })

    }

    loadProfile(){
        this.profileLoadProg = 'LOADING'
        ApiService.getMyProfile({auth: rootStore.mainStore.token,
             data:{uid: rootStore.mainStore.uid!}
            }).then(resp=>{

                fetchVIntroUrl(rootStore.mainStore.uid!).then(resp => {
                    console.log('Audio source is '+resp)
                    if(resp)
                    this.voiceIntrolUrl = resp
                })
                .catch(err => console.error(err))

                let data = resp.data
                this.myProfileResp = data

                this.name = data.name
                this.nickName = data.nick
                this.email = data.email
                this.place = data.place
                this.bio = data.bio?? ''
                this.profileVisits = data.profileVisits?? 0
                this.vIntroVisits = data.vIntroVisit?? 0
                if(data.dob != null)
                 this.dob = moment(data.dob).format(DOB_FORMAT)
                this.likesGot = data.likesGot?? 0
                this.shownCount = data.shownCount?? 0
                this.dumpCount = data.dumpCount?? 0
                this.prompts.push({q: data.prompts.q1, a: data.prompts.a1})
                this.prompts.push({q: data.prompts.q2, a: data.prompts.a2})
                this.prompts.push({q: data.prompts.q3, a: data.prompts.a3})
                this.rightSwipes = data.rightSwipes ?? 0
                this.leftSwipes = data.leftSwipes ?? 0
                this.matches = data.matches ?? 0
                this.dateEnabled = data.dateEnabled!
                this.geoLocation = data.location?? null
                rootStore.mainStore.updateNameOfUser(resp.data.name)

            }).catch(err=>{
                console.error(err)
            })
    }

    async updateProfile(){
        this.updateProfileLoadProg = 'LOADING'

        const updateReq: MyProfileUpdateRequest = {
            uid: rootStore.mainStore.uid!,
            name: this.name,
            nick: this.nickName,
            email: this.email,
            place: this.place,
            bio: this.bio,
            pic0: this.picData0 == ''? 'same': this.picData0,
            pic1: this.picData1 == ''? 'same': this.picData1,
            pic2: this.picData2 == ''? 'same': this.picData2,
            pic3: this.picData3 == ''? 'same': this.picData3,
            vIntro: this.voiceIntroData ,
            dob: moment(this.dob,DOB_FORMAT).toISOString(),
            sex: this.sex,
            prompts: {
                q1: this.prompts[0].q,
                a1: this.prompts[0].a,
                q2: this.prompts[1].q,
                a2: this.prompts[1].a,
                q3: this.prompts[2].q,
                a3: this.prompts[2].a,
            }
        }
        ApiService.updateMyProfile({
            auth: rootStore.mainStore.token,
            data: updateReq
        }).then((resp)=>{
            this.updateProfileLoadProg = 'SUCCESS'
            this.myProfileResp = resp.data
            this.picData0 = ''
            this.picData1 = ''
            this.picData2 = ''
            this.picData3 = ''
            this.voiceIntroData = ''
            rootStore.mainStore.updateNameOfUser(resp.data.name)
        }).catch(err=>{
            this.updateProfileLoadProg = 'FAILED'
        })
    }

    handleGoogleAuthEmail(resp: GoogleLoginResponse){

        const fedEmailUpdateReq: UpdateFedEmailRequest = {
            token: resp.tokenId
        }

        ApiService.updateFedEmail(fedEmailUpdateReq)
            .then(result =>{
                this.email = result.data.email
            })
            .catch(err =>{
                Logger.err(err, 'FED_EMAIL_UPDATE_FAILED')
            })
    }

    delinkEmail(){
        ApiService.delinkEmail()
            .then(resp=>{
                this.email = null
            })
    }

    deleteProfile(){
        this.deleteProfileProg = 'LOADING'
        ApiService.deleteProfile().then(resp=>{
            this.deleteProfileProg = 'SUCCESS'
            console.log(resp.data)
        }).catch(err=>{
            console.error(err)
        })
    }



}